// PTZ / 追蹤（cluster 0x8060, spec 21 §3.8）。設定頁承載:
//   - ObjectTracking（toggle, ATTR SET bool）
//   - Zoom（型別自適應 widget:int→slider / enum→segmented;GAP-DS-6 過渡自適應）
//   - Calibrate（button + 單次確認;進行中 disable 其他 PTZ;收 MoveStop 事件解除）
// 方向盤/手勢即時控制屬 Live 域（DeviceLivePage），設定頁不渲染（spec 21 §3.8）。

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signaling } from '../../../lib/ws';
import type { SignalFrame } from '../../../lib/ws';
import { PTZ_CALIBRATE, PTZ_OBJECT_TRACKING, PTZ_ZOOM } from './ids';
import { opCmd, useAttr } from './ops';
import { ConfirmDialog, SectionCard, SegmentedRow, SliderRow, ToggleRow } from './widgets';
import {
  isCalibrateClearEvent,
  nextFreePresetSlot,
  parsePresets,
  presetStorageKey,
  PTZ_CLEAR_PRESET,
  PTZ_GOTO_PRESET,
  PTZ_SET_PRESET,
  removePreset,
  upsertPreset,
  zoomKind,
  ZOOM_INT_MAX,
  ZOOM_INT_MIN,
  type PtzPreset,
} from '../ptz';

export function PtzSection({ deviceId, wsOpen }: { deviceId: string; wsOpen: boolean }) {
  const { t } = useTranslation();
  const tracking = useAttr(deviceId, PTZ_OBJECT_TRACKING, wsOpen);
  const zoom = useAttr(deviceId, PTZ_ZOOM, wsOpen);

  const [confirmCalibrate, setConfirmCalibrate] = useState(false);
  const [calibrating, setCalibrating] = useState(false);
  const [calibrateMsg, setCalibrateMsg] = useState<string | null>(null);

  // 預置點（APP#47）：device 存位置、client 存名稱（per device_id, localStorage）。
  const [presets, setPresets] = useState<PtzPreset[]>([]);
  const [adding, setAdding] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetBusy, setPresetBusy] = useState(false);
  const [presetError, setPresetError] = useState<string | null>(null);

  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // 校正進行中:收 MoveStop 事件（0x...0100）即解除（spec 21 §3.8）。
  useEffect(() => {
    if (!calibrating) return;
    const off = signaling.on('event', (frame: SignalFrame) => {
      if (frame.device_id !== deviceId) return;
      const eventId = (frame.id ?? frame.event_id ?? (frame.payload as { id?: string; event_id?: string } | undefined)?.id ?? (frame.payload as { event_id?: string } | undefined)?.event_id) as
        | string
        | undefined;
      if (isCalibrateClearEvent(eventId)) {
        if (!alive.current) return;
        setCalibrating(false);
        setCalibrateMsg(t('deviceSettings.ptzCalibrateDone'));
      }
    });
    return off;
  }, [calibrating, deviceId, t]);

  // 載入 client-local 預置點名稱（device 端只存位置，名稱字典無 attr → 存本地）。
  useEffect(() => {
    try {
      setPresets(parsePresets(localStorage.getItem(presetStorageKey(deviceId))));
    } catch {
      setPresets([]);
    }
  }, [deviceId]);

  if (!wsOpen) {
    return (
      <SectionCard title={t('deviceSettings.ptz')}>
        <p className="text-secondary" style={{ margin: 0 }}>
          {t('deviceSettings.connecting')}
        </p>
      </SectionCard>
    );
  }

  // 進行中 disable 其他 PTZ 操作（spec 21 §3.8）。
  const ptzBusy = calibrating;
  const kind = zoomKind(zoom.value);

  async function runCalibrate() {
    setConfirmCalibrate(false);
    setCalibrateMsg(null);
    setCalibrating(true);
    try {
      await opCmd(deviceId, PTZ_CALIBRATE);
      // ACK 僅代表「已開始」;真正完成由 MoveStop 事件解除。送出失敗才回退。
    } catch (err) {
      if (!alive.current) return;
      setCalibrating(false);
      setCalibrateMsg(err instanceof Error ? t('deviceSettings.ptzCalibrateFailedReason', { msg: err.message }) : t('deviceSettings.ptzCalibrateFailed'));
    }
  }

  function persistPresets(next: PtzPreset[]) {
    setPresets(next);
    try {
      localStorage.setItem(presetStorageKey(deviceId), JSON.stringify(next));
    } catch {
      /* localStorage 不可用（隱私模式）→ 僅記憶體 */
    }
  }

  // 新增：以下一個可用 slot 對 device SetPreset（存目前鏡頭位置），成功後把名稱寫本地。
  async function addPreset() {
    const name = presetName.trim();
    if (name.length < 1 || name.length > 24) {
      setPresetError(t('deviceSettings.ptzPresetNameError'));
      return;
    }
    const slot = nextFreePresetSlot(presets);
    if (slot == null) {
      setPresetError(t('deviceSettings.ptzPresetFull'));
      return;
    }
    setPresetBusy(true);
    setPresetError(null);
    try {
      await opCmd(deviceId, PTZ_SET_PRESET, slot);
      if (!alive.current) return;
      persistPresets(upsertPreset(presets, slot, name));
      setPresetName('');
      setAdding(false);
    } catch (err) {
      if (!alive.current) return;
      setPresetError(err instanceof Error ? err.message : t('deviceSettings.setFailed'));
    } finally {
      if (alive.current) setPresetBusy(false);
    }
  }

  async function gotoPreset(slot: number) {
    setPresetError(null);
    try {
      await opCmd(deviceId, PTZ_GOTO_PRESET, slot);
    } catch (err) {
      if (!alive.current) return;
      setPresetError(err instanceof Error ? err.message : t('deviceSettings.setFailed'));
    }
  }

  async function clearPreset(slot: number) {
    setPresetBusy(true);
    setPresetError(null);
    try {
      await opCmd(deviceId, PTZ_CLEAR_PRESET, slot);
      if (!alive.current) return;
      persistPresets(removePreset(presets, slot));
    } catch (err) {
      if (!alive.current) return;
      setPresetError(err instanceof Error ? err.message : t('deviceSettings.setFailed'));
    } finally {
      if (alive.current) setPresetBusy(false);
    }
  }

  return (
    <SectionCard title={t('deviceSettings.ptz')}>
      <ToggleRow label={t('deviceSettings.ptzObjectTracking')} hint={t('deviceSettings.ptzObjectTrackingHint')} attr={tracking} disabled={ptzBusy} />

      {/* Zoom 型別自適應（GAP-DS-6）:int→slider / enum→segmented;未知值（未 report）隱藏 */}
      {kind === 'int' && (
        <SliderRow label={t('deviceSettings.ptzZoom')} hint={t('deviceSettings.ptzZoomHint')} attr={zoom} min={ZOOM_INT_MIN} max={ZOOM_INT_MAX} unit="×" disabled={ptzBusy} />
      )}
      {kind === 'enum' && (
        <SegmentedRow
          label={t('deviceSettings.ptzZoom')}
          hint={t('deviceSettings.ptzZoomHint')}
          attr={zoom}
          disabled={ptzBusy}
          options={[
            [0, t('deviceSettings.ptzZoomNear')],
            [1, t('deviceSettings.ptzZoomFar')],
          ]}
        />
      )}

      {/* 預置點（APP#47）：device 存位置、client 存名稱;owner-only */}
      {(
        <div style={{ borderTop: '1px solid var(--color-surface-line)', paddingTop: 'var(--spacing-5)', display: 'grid', gap: 'var(--spacing-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-4)' }}>
            <div>
              <div>{t('deviceSettings.ptzPresets')}</div>
              <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                {t('deviceSettings.ptzPresetHint')}
              </div>
            </div>
            {!adding && (
              <button
                className="btn-ghost"
                style={{ padding: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}
                disabled={ptzBusy}
                onClick={() => {
                  setAdding(true);
                  setPresetError(null);
                }}
              >
                {t('deviceSettings.ptzPresetAdd')}
              </button>
            )}
          </div>

          {presets.length === 0 && !adding && (
            <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
              {t('deviceSettings.ptzPresetEmpty')}
            </p>
          )}

          {presets.map((p) => (
            <div key={p.slot} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-4)' }}>
              <button
                className="btn-ghost"
                disabled={ptzBusy}
                onClick={() => void gotoPreset(p.slot)}
                style={{ flex: 1, textAlign: 'left', padding: 'var(--spacing-2) var(--spacing-3)' }}
              >
                <span aria-hidden="true">📍</span> {p.name}
                <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', marginLeft: 'var(--spacing-3)' }}>
                  #{p.slot}
                </span>
              </button>
              <button
                className="btn-ghost"
                disabled={presetBusy}
                onClick={() => void clearPreset(p.slot)}
                style={{ padding: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}
              >
                {t('deviceSettings.ptzPresetDelete')}
              </button>
            </div>
          ))}

          {adding && (
            <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                value={presetName}
                maxLength={24}
                placeholder={t('deviceSettings.ptzPresetNamePlaceholder')}
                aria-label={t('deviceSettings.ptzPresetName')}
                onChange={(e) => setPresetName(e.target.value)}
                style={{ flex: 1, minWidth: 140 }}
              />
              <button className="btn-primary" disabled={presetBusy} onClick={() => void addPreset()}>
                {presetBusy ? t('common.saving') : t('deviceSettings.ptzPresetSave')}
              </button>
              <button
                className="btn-ghost"
                disabled={presetBusy}
                onClick={() => {
                  setAdding(false);
                  setPresetName('');
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          )}

          {presetError && (
            <p className="error-text" role="alert" style={{ margin: 0 }}>
              {presetError}
            </p>
          )}
        </div>
      )}

      {/* Calibrate（button + 單次確認;進行中 disable + 顯示狀態，收 MoveStop 解除） */}
      {(
        <div style={{ borderTop: '1px solid var(--color-surface-line)', paddingTop: 'var(--spacing-5)', display: 'grid', gap: 'var(--spacing-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-6)', flexWrap: 'wrap' }}>
            <div>
              <div>{t('deviceSettings.ptzCalibrate')}</div>
              <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                {t('deviceSettings.ptzCalibrateHint')}
              </div>
            </div>
            <button className="btn-ghost" disabled={!wsOpen || calibrating} onClick={() => setConfirmCalibrate(true)}>
              {calibrating ? t('deviceSettings.ptzCalibrating') : t('deviceSettings.ptzCalibrate')}
            </button>
          </div>
          {calibrating && (
            <p className="text-secondary" role="status" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
              <span aria-hidden="true">🎯</span> {t('deviceSettings.ptzCalibratingNote')}
            </p>
          )}
          {calibrateMsg && (
            <p className="text-secondary" role="status" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
              {calibrateMsg}
            </p>
          )}
        </div>
      )}

      {confirmCalibrate && (
        <ConfirmDialog
          spec={{
            title: t('deviceSettings.ptzCalibrateConfirmTitle'),
            body: t('deviceSettings.ptzCalibrateConfirmBody'),
            confirmLabel: t('deviceSettings.ptzCalibrateStart'),
          }}
          onCancel={() => setConfirmCalibrate(false)}
          onConfirm={() => void runCalibrate()}
        />
      )}
    </SectionCard>
  );
}
